import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { useNavigate } from "react-router-dom";
import { Plus, Minus, Check, X, AlertTriangle, ArrowLeft, Search, RefreshCw, Boxes, Truck, MapPin, Package, CheckCircle2, Printer } from "lucide-react";
import { useConfirm } from "@/components/ConfirmModal";

/**
 * Yard Tablet — Stock + Dockets, tabbed.
 *
 * Tab 1: Quick Stock Update (existing chunky card list with ± chips).
 * Tab 2: Today's Dockets — chunky cards a warehouse hand can read at a
 *        glance, with a big "Confirm Pickup" button per docket that hits
 *        POST /api/dockets/{id}/confirm-pickup.
 */
export default function YardStockUpdatePage() {
  const toast = useToast();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem("yard-tab") || "stock"; }
    catch { return "stock"; }
  });
  const switchTab = (t) => {
    setTab(t);
    try { localStorage.setItem("yard-tab", t); } catch (_) {}
  };

  // ─── Stock state ─────────────────────────────────────────────────────
  const [items, setItems] = useState([]);
  const [loadingStock, setLoadingStock] = useState(true);
  const [q, setQ] = useState("");

  // ─── Dockets state ───────────────────────────────────────────────────
  const [dockets, setDockets] = useState([]);
  const [companies, setCompanies] = useState({});
  const [products, setProducts] = useState({});
  const [loadingDockets, setLoadingDockets] = useState(true);
  const [docketQ, setDocketQ] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);

  const loadStock = async () => {
    setLoadingStock(true);
    try {
      const { data } = await api.get("/inventory");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoadingStock(false); }
  };

  const loadDockets = async () => {
    setLoadingDockets(true);
    try {
      const [d, c, p] = await Promise.all([
        api.get("/dockets"),
        api.get("/companies"),
        api.get("/inventory"),
      ]);
      setDockets(d.data || []);
      setCompanies(Object.fromEntries((c.data || []).map((x) => [x.id, x])));
      setProducts(Object.fromEntries((p.data || []).map((x) => [x.id, x])));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoadingDockets(false); }
  };

  // Eager load both lists; switching tabs feels instant. Cheap calls.
  useEffect(() => { loadStock(); loadDockets(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => (tab === "stock" ? loadStock() : loadDockets());
  const loading = tab === "stock" ? loadingStock : loadingDockets;

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((it) =>
      (it.Product_Name || "").toLowerCase().includes(t) ||
      (it.SKU || "").toLowerCase().includes(t) ||
      (it.Category || "").toLowerCase().includes(t),
    );
  }, [items, q]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const visibleDockets = useMemo(() => {
    const needle = docketQ.trim().toLowerCase();
    return (dockets || [])
      .filter((d) => {
        if (!showCompleted && (d.Docket_Status === "Picked Up" || d.Docket_Status === "Cancelled")) return false;
        if (showCompleted && d.Docket_Status !== "Picked Up") return false;
        if (!needle) return true;
        const co = companies[d.Company_ID]?.Company_Name || "";
        const pr = products[d.Product_ID]?.Product_Name || "";
        return [co, pr, d.Docket_Number, d.Delivery_Address]
          .filter(Boolean).join(" ").toLowerCase().includes(needle);
      })
      .sort((a, b) => (a.Pickup_Deadline || "").localeCompare(b.Pickup_Deadline || ""));
  }, [dockets, docketQ, showCompleted, companies, products]);

  const confirmPickup = async (docket) => {
    const co = companies[docket.Company_ID]?.Company_Name || "this customer";
    const pr = products[docket.Product_ID]?.Product_Name || "the items";
    const ok = await confirm({
      title: "Confirm pickup?",
      message: `Mark ${docket.Quantity_To_Pick_Up} × ${pr} for ${co} as Picked Up?`,
      confirmLabel: "Confirm pickup",
    });
    if (!ok) return;
    try {
      await api.post(`/dockets/${docket.id}/confirm-pickup`);
      toast.success("Pickup confirmed");
      setDockets((prev) => prev.map((d) =>
        d.id === docket.id ? { ...d, Docket_Status: "Picked Up" } : d
      ));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const updateItem = (fresh) =>
    setItems((prev) => prev.map((x) => (x.id === fresh.id ? fresh : x)));

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-zinc-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            data-testid="yard-back-btn"
            className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-700"
            aria-label="Back to dashboard"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900 leading-tight">Yard Tablet</h1>
            <p className="text-xs text-zinc-500">
              {tab === "stock" ? "Tap a number to type, or use the ± chips." : "Tap Confirm Pickup once a customer collects."}
            </p>
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            data-testid="yard-refresh-btn"
            className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-700 disabled:opacity-50"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Tabs — tablet-sized */}
        <div className="max-w-3xl mx-auto px-4 pb-2">
          <div className="flex gap-2 p-1 bg-zinc-100 rounded-xl">
            <button
              data-testid="yard-tab-stock"
              onClick={() => switchTab("stock")}
              className={[
                "flex-1 h-11 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors",
                tab === "stock" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-600 hover:text-zinc-900",
              ].join(" ")}
            >
              <Boxes className="w-4 h-4" /> Stock
            </button>
            <button
              data-testid="yard-tab-dockets"
              onClick={() => switchTab("dockets")}
              className={[
                "flex-1 h-11 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors",
                tab === "dockets" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-600 hover:text-zinc-900",
              ].join(" ")}
            >
              <Truck className="w-4 h-4" /> Dockets
              {dockets.filter((d) => d.Docket_Status === "Pending" || d.Docket_Status === "Scheduled").length > 0 && (
                <span
                  className="ml-1 text-[11px] font-bold bg-brand-600 text-white px-1.5 rounded-full min-w-[20px] text-center"
                  data-testid="yard-dockets-badge"
                >
                  {dockets.filter((d) => d.Docket_Status === "Pending" || d.Docket_Status === "Scheduled").length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Search bar (context-switches by tab) */}
        <div className="max-w-3xl mx-auto px-4 pb-3">
          <div className="relative">
            <Search className="w-5 h-5 text-zinc-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              data-testid={tab === "stock" ? "yard-search" : "yard-docket-search"}
              value={tab === "stock" ? q : docketQ}
              onChange={(e) => (tab === "stock" ? setQ(e.target.value) : setDocketQ(e.target.value))}
              placeholder={tab === "stock" ? "Search product, SKU or category…" : "Search customer, product, address…"}
              className="w-full h-12 pl-12 pr-4 text-base rounded-xl border border-zinc-300 bg-white focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
              type="search"
              autoComplete="off"
            />
          </div>
          {tab === "dockets" && (
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => setShowCompleted(false)}
                data-testid="yard-docket-filter-pending"
                className={[
                  "h-9 px-3 rounded-lg text-xs font-semibold transition-colors",
                  !showCompleted ? "bg-brand-600 text-white" : "bg-white border border-zinc-300 text-zinc-700",
                ].join(" ")}
              >To pick up</button>
              <button
                onClick={() => setShowCompleted(true)}
                data-testid="yard-docket-filter-done"
                className={[
                  "h-9 px-3 rounded-lg text-xs font-semibold transition-colors",
                  showCompleted ? "bg-brand-600 text-white" : "bg-white border border-zinc-300 text-zinc-700",
                ].join(" ")}
              >Picked up today</button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        {tab === "stock" && (<>
          {loadingStock && (
            <div className="text-center py-16 text-zinc-500 font-mono text-sm">Loading inventory…</div>
          )}
          {!loadingStock && filtered.length === 0 && (
            <div className="text-center py-16 text-zinc-500 font-mono text-sm">
              {items.length === 0 ? "No products in inventory." : `No products match “${q}”.`}
            </div>
          )}
          {!loadingStock && filtered.map((it) => (
            <StockCard key={it.id} item={it} onSaved={updateItem} />
          ))}
        </>)}

        {tab === "dockets" && (<>
          {loadingDockets && (
            <div className="text-center py-16 text-zinc-500 font-mono text-sm">Loading dockets…</div>
          )}
          {!loadingDockets && visibleDockets.length === 0 && (
            <div className="text-center py-16 text-zinc-500 font-mono text-sm">
              {showCompleted ? "No dockets picked up yet." : "Nothing to pick up — well done."}
            </div>
          )}
          {!loadingDockets && visibleDockets.map((d) => (
            <DocketCard
              key={d.id}
              docket={d}
              company={companies[d.Company_ID]}
              product={products[d.Product_ID]}
              todayStr={todayStr}
              onConfirm={confirmPickup}
            />
          ))}
        </>)}
      </div>
    </div>
  );
}

/** Tablet-friendly docket card with a chunky Confirm Pickup button. */
function DocketCard({ docket, company, product, todayStr, onConfirm }) {
  const status = docket.Docket_Status;
  const overdue = (docket.Pickup_Deadline || "") < todayStr && status !== "Picked Up";
  const isPicked = status === "Picked Up";
  return (
    <div
      data-testid={`yard-docket-${docket.id}`}
      className={[
        "bg-white border rounded-2xl shadow-sm overflow-hidden",
        isPicked ? "border-emerald-200 ring-1 ring-emerald-100" :
          overdue ? "border-red-300 ring-1 ring-red-100" : "border-zinc-200",
      ].join(" ")}
    >
      <div className="px-5 py-4 border-b border-zinc-100 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-display text-xl font-bold text-zinc-900 leading-tight truncate">
            {company?.Company_Name || "—"}
          </div>
          <div className="text-xs font-mono text-zinc-500 mt-1">
            {docket.Docket_Number || docket.id?.slice(0, 8)}
            <span className="mx-2 text-zinc-300">·</span>
            <span className={overdue ? "text-red-600 font-bold" : ""}>{docket.Pickup_Deadline || "no deadline"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            data-testid={`yard-docket-print-${docket.id}`}
            onClick={() => window.open(`/dockets/${docket.id}/print`, "_blank", "noopener")}
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-zinc-700"
            title="Preview / print docket"
            aria-label="Preview or print docket"
          >
            <Printer className="w-5 h-5" />
          </button>
          <span className={[
            "text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full",
            isPicked ? "bg-emerald-100 text-emerald-800" :
              status === "Scheduled" ? "bg-blue-100 text-blue-800" :
              overdue ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800",
          ].join(" ")}>
            {isPicked ? "Picked up" : (overdue ? "Overdue" : status)}
          </span>
        </div>
      </div>

      <div className="px-5 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3 border-b border-zinc-100">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-zinc-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Product</div>
            <div className="text-sm font-semibold text-zinc-900 truncate">{product?.Product_Name || "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Boxes className="w-4 h-4 text-zinc-400 flex-shrink-0" />
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Qty</div>
            <div className="text-xl font-mono font-bold text-zinc-900">{docket.Quantity_To_Pick_Up}</div>
          </div>
        </div>
      </div>

      {docket.Delivery_Address && (
        <div className="px-5 py-3 border-b border-zinc-100 flex items-start gap-2">
          <MapPin className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-zinc-700 leading-snug">{docket.Delivery_Address}</div>
        </div>
      )}

      {docket.External_Notes && (
        <div className="px-5 py-3 border-b border-zinc-100 text-sm text-zinc-700 bg-amber-50/40">
          {docket.External_Notes}
        </div>
      )}

      <div className="px-5 py-4">
        {isPicked ? (
          <div className="flex items-center justify-center gap-2 text-emerald-700 font-semibold py-2">
            <CheckCircle2 className="w-5 h-5" /> Pickup confirmed
          </div>
        ) : (
          <button
            data-testid={`yard-docket-confirm-${docket.id}`}
            onClick={() => onConfirm(docket)}
            className="w-full h-14 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-display text-lg font-bold inline-flex items-center justify-center gap-2 shadow-sm transition-colors"
          >
            <CheckCircle2 className="w-5 h-5" />
            Confirm Pickup
          </button>
        )}
      </div>
    </div>
  );
}

function StockCard({ item, onSaved }) {
  const total = Number(item.Total_Stock || 0);
  const alloc = Number(item.Allocated_Stock || 0);
  const min = Number(item.Min_Stock_Warning || 0);
  const available = Math.max(0, total - alloc);
  const low = available <= min;
  const pallets = item.Units_Per_Pallet > 0 ? Math.floor(total / item.Units_Per_Pallet) : null;

  return (
    <div
      data-testid={`yard-card-${item.id}`}
      className={[
        "bg-white border rounded-2xl shadow-sm overflow-hidden",
        low ? "border-red-300 ring-1 ring-red-100" : "border-zinc-200",
      ].join(" ")}
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-zinc-100 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-display text-xl font-bold text-zinc-900 leading-tight">{item.Product_Name}</div>
          <div className="text-xs font-mono text-zinc-500 mt-1">
            {item.SKU || "—"}{item.Category ? ` · ${item.Category}` : ""}{item.Colour ? ` · ${item.Colour}` : ""}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className={`text-2xl font-mono font-bold ${low ? "text-red-600" : "text-green-700"}`}>
            {available.toLocaleString()}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold flex items-center gap-1 justify-end">
            {low && <AlertTriangle className="w-3 h-3 text-red-500" />} available
          </div>
        </div>
      </div>

      {/* Bumpers */}
      <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4 border-b border-zinc-100">
        <Bumper
          label="Total Stock"
          value={total}
          itemId={item.id}
          field="Total_Stock"
          onSaved={onSaved}
          steps={[1, 5, 10, 50]}
        />
        <Bumper
          label="Allocated"
          value={alloc}
          itemId={item.id}
          field="Allocated_Stock"
          onSaved={onSaved}
          steps={[1, 5, 10, 50]}
        />
      </div>

      {/* Footer line */}
      <div className="px-5 py-2.5 bg-zinc-50 grid grid-cols-3 gap-2 text-[11px] uppercase tracking-wider font-semibold text-zinc-500">
        <div>Pallets <span className="block text-base font-mono text-zinc-900 normal-case tracking-normal">{pallets ?? "—"}</span></div>
        <div>Min warn <span className="block text-base font-mono text-zinc-900 normal-case tracking-normal">{min.toLocaleString()}</span></div>
        <div className="text-right">MSRP <span className="block text-base font-mono text-zinc-900 normal-case tracking-normal">${Number(item.MSRP || 0).toFixed(0)}</span></div>
      </div>
    </div>
  );
}

function Bumper({ label, value, itemId, field, onSaved, steps }) {
  const toast = useToast();
  const [draft, setDraft] = useState(String(value));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sync down if outer value changes (e.g. after a ± step saved)
  useEffect(() => { if (!editing) setDraft(String(value)); }, [value, editing]);

  const persist = async (next) => {
    if (Number.isNaN(next) || next === value) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/inventory/${itemId}`, { [field]: next });
      onSaved?.(data);
      toast.success(`${label} → ${next.toLocaleString()}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setDraft(String(value));
    } finally {
      setSaving(false);
    }
  };

  const bump = (delta) => {
    const next = Math.max(0, Math.round(value + delta));
    persist(next);
  };

  const commitDraft = () => {
    setEditing(false);
    const n = Number(draft);
    if (Number.isNaN(n)) { setDraft(String(value)); return; }
    persist(Math.max(0, Math.round(n)));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">{label}</span>
        {editing && (
          <button
            onClick={() => { setEditing(false); setDraft(String(value)); }}
            className="text-[11px] text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
            data-testid={`yard-${field}-cancel-${itemId}`}
          >
            <X className="w-3 h-3" /> cancel
          </button>
        )}
      </div>

      <div className="flex items-stretch gap-2">
        {/* − button */}
        <button
          onClick={() => bump(-1)}
          disabled={saving || value <= 0}
          data-testid={`yard-${field}-minus-${itemId}`}
          className="w-12 h-14 rounded-xl bg-zinc-100 hover:bg-zinc-200 active:bg-zinc-300 text-zinc-700 flex items-center justify-center disabled:opacity-40"
          aria-label={`Decrease ${label}`}
        >
          <Minus className="w-6 h-6" />
        </button>

        {/* Big number / input */}
        {editing ? (
          <input
            data-testid={`yard-${field}-input-${itemId}`}
            type="number"
            inputMode="numeric"
            autoFocus
            value={draft}
            disabled={saving}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitDraft}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
              if (e.key === "Escape") { setEditing(false); setDraft(String(value)); }
            }}
            className="flex-1 h-14 text-center text-2xl font-mono font-bold border-2 border-brand-500 rounded-xl focus:outline-none focus:ring-4 focus:ring-brand-100"
          />
        ) : (
          <button
            onClick={() => { setEditing(true); setDraft(String(value)); }}
            data-testid={`yard-${field}-display-${itemId}`}
            disabled={saving}
            className="flex-1 h-14 rounded-xl bg-zinc-50 hover:bg-white border border-zinc-200 hover:border-brand-300 text-2xl font-mono font-bold text-zinc-900 transition-colors disabled:opacity-50"
          >
            {value.toLocaleString()}
          </button>
        )}

        {/* + button */}
        <button
          onClick={() => bump(1)}
          disabled={saving}
          data-testid={`yard-${field}-plus-${itemId}`}
          className="w-12 h-14 rounded-xl bg-brand-100 hover:bg-brand-200 active:bg-brand-300 text-brand-800 flex items-center justify-center disabled:opacity-40"
          aria-label={`Increase ${label}`}
        >
          <Plus className="w-6 h-6" />
        </button>
      </div>

      {/* Bigger-step chips */}
      <div className="mt-2 flex gap-1.5 flex-wrap">
        {steps.slice(1).flatMap((s) => [
          <StepChip key={`m${s}`} label={`-${s}`} onClick={() => bump(-s)} negative data-testid={`yard-${field}-step-minus-${s}-${itemId}`} />,
          <StepChip key={`p${s}`} label={`+${s}`} onClick={() => bump(s)} data-testid={`yard-${field}-step-plus-${s}-${itemId}`} />,
        ])}
        {editing && (
          <button
            onClick={commitDraft}
            disabled={saving}
            data-testid={`yard-${field}-save-${itemId}`}
            className="ml-auto px-3 h-8 rounded-full bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Check className="w-3.5 h-3.5" /> Save
          </button>
        )}
      </div>
    </div>
  );
}

function StepChip({ label, onClick, negative, ...rest }) {
  return (
    <button
      onClick={onClick}
      {...rest}
      className={[
        "h-8 px-3 rounded-full text-xs font-mono font-bold border transition-colors",
        negative
          ? "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100"
          : "bg-brand-50 text-brand-800 border-brand-200 hover:bg-brand-100",
      ].join(" ")}
    >
      {label}
    </button>
  );
}
